import * as React from 'react';
import { TextField, CircularProgress, makeStyles, createStyles, Typography, Popper } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { IEmployeesAutocompleteParams, ResourceRelationsService } from '../services/ResourceRelationsService';
import strings from './../utils/StringManager';
import ResourcesAutocompleteSearch from './ResourcesAutocompleteSearch';
import { IGridResourcesConfig } from './Relations/GridResourcesConfig';
import clsx from 'clsx';
import { useGetResourceGridApi } from '../services/useResourcesForGridApi';
import { parseGridOptionsForAutocomplete } from './ResourcesAutocompleteMI';

const useStyles = makeStyles((theme) =>
	createStyles({
		employeeComboxRoot: {
			display: 'flex',
			alignItems: 'center',
		},
		employeeComboxAutocomplete: {
			flex: '1',
		},
		employeeComboxSearch: {
			marginLeft: '7px',
		},
	}),
);

export interface OptionType {
	value: string,
	label: string,
	description?: string,
	kind: string,
}

export interface IAutocompleteResult {
	options: OptionType[],
	searchText: string,
}

export interface IEmployeesFilterParams {
	maxRows: number,
	forAcceptant: boolean,
	subordinatesTree: boolean;
	filterId: string,
}

function dataMapper(item: { id: number, value: string, label: string }): OptionType {

	return {
		value: item.id.toString(),
		label: item.label,
		kind: "Employee",
		description: "",
	};
}

interface IProps {
	value: OptionType,
	onSelect: (value: OptionType) => void,
	filters: IEmployeesFilterParams,

	placeholderText?: string,
	grid?: IGridResourcesConfig,
}

export default function EmployeesComboboxMI(props: IProps) {
	const [loading, setLoading] = React.useState(false);
	const [options, setOptions] = React.useState<OptionType[]>([]);
	const [inputValue, setInputValue] = React.useState('');
	const classes = useStyles();

	const [setRequest, resourceGridData, isLoading, isError] = useGetResourceGridApi();

	React.useEffect(() => {
		let active = true;

		if (inputValue === '') {
			setOptions([]);
			setLoading(false);
			return undefined;
		}

		setLoading(true);

		fetch(inputValue, (results?: OptionType[]) => {
			if (active) {
				var items = results || [];
				setOptions(items);
			}
			setLoading(false);
		});

		return () => {
			active = false;
		};
	}, [inputValue]);

	React.useEffect(() => {
		if (!resourceGridData)
			return;

		if (resourceGridData.searchText === inputValue) {
			let items = parseGridOptionsForAutocomplete(resourceGridData.rows, "Employee", props.grid);

			setLoading(false);
			setOptions(items);
		}
	}, [resourceGridData]);

	function fetch(searchValue: string, callback: (results?: OptionType[]) => void) {

		if (!props.filters.filterId) {
			let template = props.grid?.labelTemplate;
			let searchFields: string[] = [];
			if (template) {
				searchFields = props.grid.columns
					.filter(x =>
						x.searched && template.indexOf('{' + x.id + '}') > -1
					)
					.map(x => x.id);
			} else {
				searchFields = ['name'];
			}

			setRequest({
				dir: 'Asc',
				filters: getFiltersGrid(),
				kind: 'Employee',
				orderBy: 'name',
				search: searchValue,
				skip: 0,
				take: props.filters.maxRows,
				searchFields: searchFields
			})
		}
		else {

			let service = new ResourceRelationsService();
			let params: IEmployeesAutocompleteParams = {
				...props.filters,
				term: searchValue,
				filters: getFiltersGrid()
			};
			service.getEmployeesAutocomplete(params, (data) => {
				if (inputValue === data.searchText) {
					let results: OptionType[] = data.options.map(dataMapper);
					callback(results);
				}
			});
		}
	};

	let noOptionsText = strings.get("enterTextToSearch") + "...";
	if (inputValue) {
		noOptionsText = strings.format("noOptionsFoundForPhrase", inputValue);
	}

	let placeholder = strings.get("search") + "...";
	if (props.placeholderText) {
		placeholder = props.placeholderText;
	};

	function getFiltersGrid() {
		let filters = props.grid?.filters ?? "";

		if (props.filters.forAcceptant) {
			filters += '&forAcceptant';
		}
		if (props.filters.subordinatesTree) {
			filters += '&subordinatesTree';
		}

		return filters;
	}

	const renderOption = (option: OptionType) => (<div>
		<Typography variant="body1" color="textPrimary" noWrap>{option.label}</Typography>
		<Typography variant="body2" color="textSecondary" noWrap>
			{option.description}
		</Typography>
	</div>);

	return <div className={clsx(classes.employeeComboxRoot, "mi-lab-combobox")} >
		<Autocomplete
			className={clsx(classes.employeeComboxAutocomplete, "mi-lab-autocomplete-outlined")}
			classes={{
				clearIndicator: "mi-icon-button",
				popupIndicator: "mi-icon-button"
			}}
			value={props.value}
			onChange={(event, newValue) => {
				let v = newValue as OptionType;
				props.onSelect(v);
			}}
			getOptionLabel={(option: OptionType) => option.label}
			getOptionSelected={(option: OptionType, value: OptionType) => option.value == value.value}
			options={options}
			loading={loading}
			filterOptions={x => x}
			autoComplete
			includeInputInList

			noOptionsText={noOptionsText}

			renderInput={params => (
				<TextField
					{...params}
					variant="outlined"
					fullWidth
					onChange={(event) => setInputValue(event.target.value)}
					InputProps={{
						...params.InputProps,
						autoComplete: 'disabled',
						placeholder: placeholder,
						endAdornment: (
							<React.Fragment>
								{loading ? <CircularProgress color="inherit" size={20} /> : null}
								{params.InputProps.endAdornment}
							</React.Fragment>
						),
					}}
				/>
			)}

			PopperComponent={PopperMy}
			renderOption={renderOption}
		/>
		{props.grid && !props.grid.hidden && <ResourcesAutocompleteSearch
			className={classes.employeeComboxSearch}
			kind={"Employee"}
			onSelect={(v) => {
				if (v[0])
					props.onSelect(v[0]);
				else
					props.onSelect(null);
			}}
			value={[props.value]}
			multiple={false}

			columns={props.grid.columns}
			filters={getFiltersGrid()}
			title={props.grid.title}
			
			rowsPerPageOptions={props.grid.rowsPerPageOptions}
		/>}
	</div>;
}

const PopperMy = function (props) {
	return (<Popper {...props} style={{ width: 'auto' }} placement='bottom-start' />)
}